/**
 * BookingForm Component
 *
 * Class enrollment request form with validation
 * Following PRP specification lines 1360-1550
 */

import { useForm } from 'react-hook-form';
import { useState, useRef, useEffect } from 'react';
import { DayPicker } from 'react-day-picker';
import { format } from 'date-fns';
import 'react-day-picker/dist/style.css';
import { Input } from '../forms/Input';
import { Select } from '../forms/Select';
import { Textarea } from '../forms/Textarea';
import { Button } from '../Button';
import type { BookingFormData } from '../../types/booking';
import type { EmailFunctionResponse, EmailErrorResponse } from '../../types/email';
import { courses } from '../../data/courses';

export const BookingForm: React.FC = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // REACT HOOK FORM: Initialize with defaults
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    trigger,
    formState: { errors }
  } = useForm<BookingFormData>({
    defaultValues: {
      fullName: '',
      email: '',
      phone: '',
      preferredLocation: 'orlando',
      preferredCourse: '',
      preferredDates: '',
      numberOfAttendees: 1,
      specialRequests: ''
    }
  });

  // DATE RULES: classes run Tue/Thu/Fri/Sat, and must be at least one week out.
  // Allowed weekdays via Date.getDay(): Tue=2, Thu=4, Fri=5, Sat=6.
  // These are enforced by physically disabling invalid days in the calendar
  // (below) AND on the server (netlify/functions/utils/validators.js).
  const ALLOWED_DAYS = new Set([2, 4, 5, 6]);
  const minDate = (() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + 7);
    return d;
  })();

  // Selected date is stored as an ISO YYYY-MM-DD string in the form.
  const selectedDateStr = watch('preferredDates');
  const selectedDate = selectedDateStr
    ? (() => {
        const [y, m, d] = selectedDateStr.split('-').map(Number);
        return new Date(y, m - 1, d);
      })()
    : undefined;

  // Calendar popover open/close + outside-click handling.
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const calendarRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!isCalendarOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target as Node)) {
        setIsCalendarOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isCalendarOpen]);

  // A day is disabled (greyed out / unclickable) when it is before the
  // one-week minimum OR not a class weekday.
  const isDayDisabled = (day: Date) =>
    day < minDate || !ALLOWED_DAYS.has(day.getDay());

  const handleDaySelect = (day: Date | undefined) => {
    if (!day) return;
    setValue('preferredDates', format(day, 'yyyy-MM-dd'), {
      shouldValidate: true,
      shouldDirty: true
    });
    setIsCalendarOpen(false);
    void trigger('preferredDates');
  };

  // Keep the field registered for validation (belt-and-suspenders: the calendar
  // already blocks invalid days, but this guards against any programmatic value).
  register('preferredDates', { required: 'Please select a class date' });

  // SUBMIT HANDLER: Call Netlify function to send emails
  const onSubmit = async (data: BookingFormData) => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Call Netlify Function to send emails
      const response = await fetch('/.netlify/functions/send-booking-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      const result: EmailFunctionResponse | EmailErrorResponse = await response.json();

      if (!response.ok || !result.success) {
        const errorMsg = 'error' in result ? result.error : 'Failed to send request';
        throw new Error(errorMsg);
      }

      // SUCCESS: Show message, reset form
      console.log('Booking emails sent:', result);
      setSubmitSuccess(true);
      reset();

      // RESET SUCCESS MESSAGE: After 5 seconds
      setTimeout(() => setSubmitSuccess(false), 5000);
    } catch (error) {
      console.error('Booking submission error:', error);
      setSubmitError(
        error instanceof Error
          ? error.message
          : 'We\'re having trouble processing your request. Please try again or call us directly.'
      );

      // Auto-hide error after 10 seconds
      setTimeout(() => setSubmitError(null), 10000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto bg-white p-8 border-4 border-rich_black-500 shadow-[8px_8px_0px_0px_rgba(2,5,18,1)]" data-aos="fade-up">
      <h2 className="font-display text-2xl md:text-3xl uppercase tracking-wider font-bold text-indigo_dye-500 mb-6">Request Class Enrollment</h2>

      {/* SUCCESS MESSAGE: Show after submission */}
      {submitSuccess && (
        <div className="mb-6 p-4 bg-green-100 text-green-800 border-3 border-green-600 animate-slideInFromTop">
          <div className="flex items-center gap-2">
            <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span className="font-mono text-sm uppercase font-semibold">Thank you! We'll contact you within 24 hours to confirm your class.</span>
          </div>
        </div>
      )}

      {/* ERROR MESSAGE: Show if submission fails */}
      {submitError && (
        <div className="mb-6 p-4 bg-red-100 border-3 border-red-600">
          <div className="flex items-start gap-2">
            <svg className="w-6 h-6 text-red-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="flex-1">
              <p className="font-mono text-sm uppercase font-semibold text-red-800 mb-2">{submitError}</p>
              <p className="font-mono text-xs text-red-700">
                Please try again, or visit our <a href="/contact" className="underline font-semibold">contact page</a>.
              </p>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* FULL NAME: Required, min 2 chars */}
        <Input
          name="fullName"
          label="Full Name"
          type="text"
          required
          register={register('fullName', {
            required: 'Full name is required',
            minLength: { value: 2, message: 'Name must be at least 2 characters' }
          })}
          error={errors.fullName?.message}
        />

        {/* EMAIL: Required, email pattern */}
        <Input
          name="email"
          label="Email Address"
          type="email"
          required
          register={register('email', {
            required: 'Email is required',
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: 'Invalid email address'
            }
          })}
          error={errors.email?.message}
        />

        {/* PHONE: Required, flexible phone pattern */}
        <Input
          name="phone"
          label="Phone Number"
          type="tel"
          placeholder="(123) 456-7890 or 123-456-7890"
          required
          register={register('phone', {
            required: 'Phone number is required',
            pattern: {
              value: /^[+]?[1]?[-\s.]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4}$/,
              message: 'Please enter a valid phone number'
            }
          })}
          error={errors.phone?.message}
        />

        {/* LOCATION: Dropdown, required */}
        <Select
          name="preferredLocation"
          label="Preferred Location"
          required
          register={register('preferredLocation', {
            required: 'Please select a location'
          })}
          options={[
            { value: 'orlando', label: 'Orlando, FL' },
            { value: 'atlanta', label: 'Atlanta, GA' },
            { value: 'on-site', label: 'On-Site (Group Training)' }
          ]}
          error={errors.preferredLocation?.message}
        />

        {/* COURSE: Dropdown, required */}
        <Select
          name="preferredCourse"
          label="Preferred Course"
          required
          register={register('preferredCourse', {
            required: 'Please select a course'
          })}
          options={[
            { value: '', label: 'Select a course...' },
            ...courses.map(course => ({
              value: course.slug,
              label: course.name
            }))
          ]}
          error={errors.preferredCourse?.message}
        />

        {/* DATE: Calendar popover — only Tue/Thu/Fri/Sat ≥ 1 week out are selectable */}
        <div className="w-full" ref={calendarRef}>
          <label
            htmlFor="preferredDates-trigger"
            className="block font-mono text-sm uppercase tracking-wide font-semibold text-gray-900 mb-2"
          >
            Preferred Class Date
            <span className="text-amaranth_purple-500 ml-1">*</span>
          </label>
          <button
            id="preferredDates-trigger"
            type="button"
            onClick={() => setIsCalendarOpen((open) => !open)}
            aria-haspopup="dialog"
            aria-expanded={isCalendarOpen}
            className={`block w-full px-4 py-3 border-3 text-sm font-body text-left transition-all duration-200 focus:outline-none focus:border-4 focus:-translate-y-1 focus:shadow-[4px_4px_0px_0px_rgba(0,60,102,0.3)] bg-white ${
              errors.preferredDates
                ? 'border-amaranth_purple-500 text-amaranth_purple-900'
                : 'border-gray-400 text-gray-900 focus:border-indigo_dye-500 hover:bg-gray-50'
            }`}
          >
            {selectedDate ? (
              format(selectedDate, 'EEEE, MMMM d, yyyy')
            ) : (
              <span className="text-gray-500">Select a class date…</span>
            )}
          </button>

          {isCalendarOpen && (
            <div
              role="dialog"
              aria-label="Choose a class date"
              className="relative z-20 mt-2 inline-block border-4 border-rich_black-500 bg-white p-3 shadow-[8px_8px_0px_0px_rgba(2,5,18,1)]"
              style={
                {
                  '--rdp-accent-color': '#003c66',
                  '--rdp-accent-background-color': '#ffdb99',
                  '--rdp-today-color': '#a3003f',
                  '--rdp-disabled-opacity': '0.25',
                  '--rdp-selected-border': '3px solid #a3003f',
                  '--rdp-day-width': '2.5rem',
                  '--rdp-day-height': '2.5rem'
                } as React.CSSProperties
              }
            >
              <DayPicker
                mode="single"
                selected={selectedDate}
                onSelect={handleDaySelect}
                disabled={isDayDisabled}
                startMonth={minDate}
                defaultMonth={selectedDate ?? minDate}
              />
            </div>
          )}

          {errors.preferredDates ? (
            <p
              className="mt-2 font-mono text-xs uppercase tracking-wide font-bold text-amaranth_purple-600"
              role="alert"
            >
              {errors.preferredDates.message}
            </p>
          ) : (
            <p className="mt-2 font-mono text-xs text-gray-500">
              Classes run Tue, Thu, Fri &amp; Sat — only dates at least one week out are available.
            </p>
          )}
        </div>

        {/* ATTENDEES: Number input */}
        <Input
          name="numberOfAttendees"
          label="Number of Attendees"
          type="number"
          min={1}
          max={100}
          required
          register={register('numberOfAttendees', {
            required: 'Number of attendees is required',
            min: { value: 1, message: 'Must be at least 1' },
            max: { value: 100, message: 'Maximum 100 attendees' },
            valueAsNumber: true
          })}
          error={errors.numberOfAttendees?.message}
        />

        {/* SPECIAL REQUESTS: Optional textarea */}
        <Textarea
          name="specialRequests"
          label="Special Requests (Optional)"
          rows={4}
          placeholder="Any special requirements or questions?"
          register={register}
        />

        {/* SUBMIT BUTTON: Disabled while submitting */}
        <Button
          type="submit"
          variant="primary"
          size="xl"
          disabled={isSubmitting}
          className="w-full"
        >
          {isSubmitting ? 'SUBMITTING...' : 'REQUEST CLASS ENROLLMENT'}
        </Button>
      </form>
    </div>
  );
};
